<template>
  <div class="color-palette-picker">
    <a-popover
      :visible="visible"
      trigger="click"
      :placement="placement"
      overlay-class-name="color-palette-popover"
      @visibleChange="onPopoverVisibleChange"
    >
      <template slot="content">
        <div class="color-panel">
          <div class="color-section">
            <div
              class="auto-row"
              :class="{ active: !normalizedColor }"
              @click="onPickColor('')"
            >
              <span class="swatch" style="background:#ffffff;border:1px solid #d9d9d9" />
              <span>{{ autoLabel }}</span>
            </div>
          </div>

          <div class="color-section">
            <div class="color-section-title">主题颜色</div>
            <div class="theme-grid">
              <span
                v-for="(color, index) in themeColors"
                :key="'t-' + index + '-' + color"
                class="swatch"
                :class="{ active: isColorActive(color) }"
                :style="{ background: color }"
                :title="color"
                @click="onPickColor(color)"
              />
            </div>
          </div>

          <div class="color-section">
            <div class="color-section-title">标准色</div>
            <div class="standard-grid">
              <span
                v-for="(color, index) in standardColors"
                :key="'s-' + index + '-' + color"
                class="swatch"
                :class="{ active: isColorActive(color) }"
                :style="{ background: color }"
                :title="color"
                @click="onPickColor(color)"
              />
            </div>
          </div>

          <div class="more-color-row">
            <label class="more-color-btn">
              <span class="rainbow-icon" />
              <span>更多颜色</span>
              <input
                class="native-color-input"
                type="color"
                :value="normalizedColor || '#ffffff'"
                @click.stop
                @focus="onNativeColorFocus"
                @input="onNativeColorInput"
                @change="onNativeColorChange"
                @blur="onNativeColorBlur"
              >
            </label>
          </div>
        </div>
      </template>
      <div class="color-trigger">
        <span
          class="swatch trigger-swatch"
          :style="{ background: normalizedColor || '#ffffff' }"
        />
        <span class="trigger-text">{{ normalizedColor || autoLabel }}</span>
        <a-icon type="down" class="trigger-arrow" />
      </div>
    </a-popover>
    <a-input
      v-if="showInput"
      :value="value"
      class="color-input"
      :placeholder="inputPlaceholder"
      allow-clear
      @change="onInputChange"
    />
  </div>
</template>

<script>
/** Office 风格主题色：10 列 × 6 行（基色 + 深浅） */
const THEME_COLOR_COLUMNS = [
  ['#FFFFFF', '#F2F2F2', '#D9D9D9', '#BFBFBF', '#A6A6A6', '#7F7F7F'],
  ['#000000', '#7F7F7F', '#595959', '#404040', '#262626', '#0D0D0D'],
  ['#E7E6E6', '#D0CECE', '#AEAAAA', '#757070', '#3F3F3F', '#1A1A1A'],
  ['#44546A', '#D6DCE5', '#ADB9CA', '#8496B0', '#333F50', '#222A35'],
  ['#4472C4', '#D6DCE5', '#9DC3E6', '#5B9BD5', '#2F5496', '#1F3864'],
  ['#ED7D31', '#FCE4D6', '#F8CBAD', '#F4B183', '#C65911', '#833C0C'],
  ['#A5A5A5', '#EDEDED', '#DBDBDB', '#C9C9C9', '#7B7B7B', '#525252'],
  ['#FFC000', '#FFF2CC', '#FFE699', '#FFD966', '#BF9000', '#7F6000'],
  ['#5B9BD5', '#DEEBF7', '#BDD7EE', '#9DC3E6', '#2E75B6', '#1F4E79'],
  ['#70AD47', '#E2EFDA', '#C6E0B4', '#A9D08E', '#548235', '#375623']
]

const STANDARD_COLORS = [
  '#C00000', '#FF0000', '#FFC000', '#FFFF00', '#92D050',
  '#00B050', '#00B0F0', '#0070C0', '#002060', '#7030A0'
]

function flattenThemeColors (columns) {
  const list = []
  const rowCount = columns[0] ? columns[0].length : 0
  for (let row = 0; row < rowCount; row++) {
    for (let col = 0; col < columns.length; col++) {
      list.push(columns[col][row])
    }
  }
  return list
}

const THEME_COLORS = flattenThemeColors(THEME_COLOR_COLUMNS)

export default {
  name: 'ColorPalettePicker',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    /** 是否显示手动输入框 */
    showInput: {
      type: Boolean,
      default: true
    },
    inputPlaceholder: {
      type: String,
      default: '也可手动输入，如 #FF2A6A'
    },
    autoLabel: {
      type: String,
      default: '自动'
    },
    placement: {
      type: String,
      default: 'topLeft'
    }
  },
  data () {
    return {
      visible: false,
      /** 系统取色器打开中：禁止关闭色板弹层，避免拖动被中断 */
      nativePicking: false,
      themeColors: THEME_COLORS,
      standardColors: STANDARD_COLORS
    }
  },
  computed: {
    normalizedColor () {
      return String(this.value || '').trim()
    }
  },
  methods: {
    emitColor (color) {
      this.$emit('input', color || '')
      this.$emit('change', color || '')
    },
    isColorActive (color) {
      return this.normalizedColor.toLowerCase() === String(color || '').toLowerCase()
    },
    onPopoverVisibleChange (vis) {
      // 系统取色器交互时点到弹层外，不要关掉
      if (!vis && this.nativePicking) return
      this.visible = vis
    },
    onPickColor (color) {
      this.emitColor(color || '')
      this.visible = false
    },
    onNativeColorFocus () {
      this.nativePicking = true
    },
    /** 拖动过程：只同步预览，不关闭弹层 */
    onNativeColorInput (e) {
      const color = (e && e.target && e.target.value) || ''
      if (!color) return
      this.emitColor(color.toUpperCase())
    },
    /** 关闭系统取色器后：确认最终颜色并收起色板 */
    onNativeColorChange (e) {
      const color = (e && e.target && e.target.value) || ''
      if (color) this.emitColor(color.toUpperCase())
      this.nativePicking = false
      this.visible = false
    },
    onNativeColorBlur () {
      // 部分浏览器取消时不会触发 change，延后释放锁
      setTimeout(() => {
        this.nativePicking = false
      }, 0)
    },
    onInputChange (e) {
      const next = e && e.target ? e.target.value : ''
      this.emitColor(next == null ? '' : String(next))
    }
  }
}
</script>

<style scoped lang="less">
.color-palette-picker {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 140px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  user-select: none;

  &:hover {
    border-color: #fa8c16;
  }
}

.trigger-swatch {
  width: 18px;
  height: 18px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  flex-shrink: 0;
}

.trigger-text {
  flex: 1;
  font-size: 13px;
  color: #333;
}

.trigger-arrow {
  font-size: 10px;
  color: #999;
}

.color-input {
  flex: 1;
  max-width: 240px;
}

.color-panel {
  width: 232px;
  padding: 4px 0;
}

.color-section {
  margin-bottom: 8px;
}

.color-section-title {
  margin: 0 -12px 6px;
  padding: 4px 12px;
  background: #f5f5f5;
  color: #666;
  font-size: 12px;
}

.auto-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 13px;
  color: #333;

  &:hover,
  &.active {
    background: #fff7e6;
  }
}

.theme-grid,
.standard-grid {
  display: grid;
  grid-template-columns: repeat(10, 18px);
  gap: 2px;
}

.swatch {
  display: inline-block;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.12);
  cursor: pointer;

  &:hover {
    outline: 1px solid #1890ff;
    outline-offset: 1px;
    position: relative;
    z-index: 1;
  }

  &.active {
    outline: 2px solid #13c2c2;
    outline-offset: 1px;
    position: relative;
    z-index: 1;
  }
}

.more-color-row {
  margin-top: 4px;
  border-top: 1px solid #f0f0f0;
  padding-top: 6px;
}

.more-color-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 13px;
  color: #333;

  &:hover {
    background: #f5f5f5;
  }
}

.rainbow-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: conic-gradient(
    #ff0000,
    #ff9900,
    #ffff00,
    #33cc33,
    #3399ff,
    #6633ff,
    #ff0000
  );
  flex-shrink: 0;
}

.native-color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
</style>
